/**
 * The protocols with scenario types, searchable and read one page at a time; each screen draws its own row.
 * The list grows only with protocols, never with intents or templates.
 */

import { useDeferredValue, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/input';
import { useProtocols } from '@/hooks/use-promotion';
import { searchTerms } from '@/lib/search';
import type { ProtocolSummary } from './types';

const PAGE_SIZE = 20;

interface PagedProtocolListProps {
	/** Shown when no protocol has scenario types yet. */
	emptyMessage: string;
	/** One row, with the search's terms to mark in it. */
	renderRow: (protocol: ProtocolSummary, terms: string[]) => ReactNode;
}

export const PagedProtocolList = ({ emptyMessage, renderRow }: PagedProtocolListProps) => {
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	const deferredSearch = useDeferredValue(search.trim());
	const terms = searchTerms(deferredSearch);
	const { data, isLoading, isFetching } = useProtocols({ search: deferredSearch || undefined, page, limit: PAGE_SIZE });
	const protocols = data?.protocols ?? [];
	const total = data?.total ?? 0;
	const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

	return (
		<div className='flex min-h-0 min-w-0 flex-1 flex-col'>
			<div className='shrink-0 border-b border-gray-500 p-3'>
				<SearchInput
					value={search}
					onChange={(event) => {
						setSearch(event.target.value);
						setPage(1);
					}}
					placeholder='Search protocols'
					className='h-7 w-full text-xs'
				/>
			</div>
			<div className='min-h-0 flex-1 overflow-y-auto'>
				{isLoading ? (
					<p className='flex items-center gap-2 px-4 py-3 text-xs text-gray-800'>
						<Loader2 className='size-3.5 animate-spin' /> Loading protocols…
					</p>
				) : protocols.length === 0 ? (
					<p className='px-4 py-3 text-xs leading-4 text-gray-800'>
						{deferredSearch ? 'No protocol matches that search.' : emptyMessage}
					</p>
				) : (
					protocols.map((protocol) => renderRow(protocol, terms))
				)}
			</div>
			{total > PAGE_SIZE && (
				<div className='flex shrink-0 items-center justify-between border-t border-gray-500 px-3 py-2 text-xs'>
					<Button variant='ghost' size='sm' disabled={page <= 1 || isFetching} onClick={() => setPage(page - 1)}>
						Previous
					</Button>
					<span className='text-gray-800 tabular-nums'>
						{page} of {pages}
					</span>
					<Button variant='ghost' size='sm' disabled={page >= pages || isFetching} onClick={() => setPage(page + 1)}>
						Next
					</Button>
				</div>
			)}
		</div>
	);
};
